import { css } from '@emotion/css';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import {
  Button,
  ButtonSizeEnum,
  ButtonVariantEnum,
} from '../../../shared/components/Button';
import { Info } from '../../../shared/components/Info';
import { Layout } from '../../../shared/components/layout/Layout';
import { Loader } from '../../../shared/components/Loader';
import { Colors } from '../../../shared/styles/colors';
import {
  h1Style,
  h3Style,
  tabularNumbersStyle,
} from '../../../shared/styles/text';
import { ChevronLeft } from '../../../shared/assets/icons';
import { useNavigator } from '../../../routing/navigator';
import { usePaymentOrder } from '../../../shared/services/api/order';
import { http } from '../../../shared/services/http';
import { Radius } from '../../../shared/styles/tokens';
import { useState } from 'react';
import { showPrivateToast } from '../../../shared/utils/show-private-toast';

export const PaymentOrderDetailsPage = (): JSX.Element => {
  const { orderId } = useParams<{ orderId: string }>();
  const {
    data: order,
    isError,
    isLoading,
    isRefetching,
  } = usePaymentOrder(orderId ?? '0');
  const [downloading, setDownloading] = useState(false);

  const navigator = useNavigator();

  if (isLoading || isRefetching) {
    return <Loader />;
  }
  if (isError || !order) {
    return <p className={errorStyle}>Order does not exist!</p>;
  }

  const downloadInvoicePdf = async () => {
    try {
      setDownloading(true);
      const response = await http.getInvoicePdf(order.id);
      const blob = response.data;

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', `invoice_${orderId}.pdf`);

      document.body.appendChild(link);

      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading the invoice:', error);
      showPrivateToast(`The invoice can't be downloaded right now.`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Layout.Content className={historyOverviewContainerStyle}>
      <Button
        startIcon={<ChevronLeft />}
        variant={ButtonVariantEnum.TEXT}
        size={ButtonSizeEnum.LARGE}
        text={`back`}
        className={backButtonStyle}
        onClick={navigator.toPrevious}
      />
      <div>
        <h1 className={h1Style}>Overview</h1>
      </div>
      <div className={infoGroupStyle}>
        <Info
          label="Date"
          value={dayjs(
            order.invoiceDate ? order.invoiceDate : order.createdAt,
          ).format('DD.MM.YYYY. HH:mm')}
          className={infoTotalStyle}
        />
        <Info
          label="Amount"
          value={`${parseFloat(order.amount).toFixed(2)} EUR`}
          className={infoTotalStyle}
        />
      </div>
      <Button
        size={ButtonSizeEnum.MEDIUM}
        variant={ButtonVariantEnum.FILLED}
        text={'Download invoice PDF'}
        onClick={() => downloadInvoicePdf()}
        loading={downloading}
        className={pdfButtonStyle}
      />
    </Layout.Content>
  );
};

const backButtonStyle = css`
  padding-top: 0rem;
  align-self: flex-start;
`;

const errorStyle = css`
  padding: 2rem 1.5rem;
  color: ${Colors.TEXT_SECONDARY};
`;

const historyOverviewContainerStyle = css`
  display: flex;
  flex-direction: column;
  padding-top: 0.75rem;
`;

const infoGroupStyle = css`
  display: flex;
  flex-wrap: wrap;
  column-gap: 0.25rem;
  width: 100%;
  margin: 1rem 0 1.5rem;
  padding: 0.75rem 1rem;
  background: ${Colors.SURFACE};
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.md};
`;

const infoTotalStyle = css`
  > div > p {
    ${h3Style}
    ${tabularNumbersStyle}
    color: ${Colors.CAPRI_BLUE};
  }
`;

const pdfButtonStyle = css`
  align-self: flex-start;
`;
